import { ArrowDown, Check } from 'iconoir-react';
import { Star } from 'iconoir-react/solid';
import { CtaButton } from '@/components/astro/cta-button/cta-button';
import * as m from '@/paraglide/messages';

const HIGHLIGHTS = [
  ['unlimited', m.why_listening_compare_unlimited],
  ['overviews', m.why_listening_hero_highlight_overviews],
  ['devices', m.why_listening_hero_highlight_devices],
] as const;

export function HeroSection() {
  return (
    <section
      aria-labelledby='why-hero-heading'
      className='relative isolate overflow-hidden'
    >
      <div
        aria-hidden='true'
        className='absolute inset-0 -z-10 bg-[linear-gradient(to_right,rgb(21_22_26/0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgb(21_22_26/0.05)_1px,transparent_1px)] [mask-image:radial-gradient(ellipse_60%_55%_at_50%_35%,black,transparent)] bg-size-[56px_56px]'
      />
      <div
        aria-hidden='true'
        className='absolute top-24 left-1/2 -z-10 h-72 w-[min(720px,90vw)] -translate-x-1/2 rounded-full bg-azure-595/15 blur-[110px]'
      />

      <div className='mx-auto flex max-w-5xl flex-col items-center px-5 pt-10 pb-16 text-center sm:px-6 sm:pt-20 sm:pb-24 lg:pt-24 lg:pb-28'>
        <p className='inline-flex items-center gap-2 rounded-full bg-white/80 py-1.5 pr-3.5 pl-2 text-[13px] font-medium text-(--v2-chip-text) shadow-sm ring-1 ring-steel-120 backdrop-blur sm:text-sm'>
          <span className='flex items-center gap-0.5 rounded-full bg-gold-375/15 px-2 py-0.5 font-semibold text-gold-950'>
            <Star aria-hidden='true' className='size-3.5 text-gold-375' />
            4.4
          </span>
          {m.why_listening_hero_rating_short()}
        </p>

        <h1
          className='mt-6 text-[40px] leading-[1.04] font-semibold tracking-[-0.045em] text-balance text-(--v2-text-primary) sm:mt-8 sm:text-6xl lg:text-7xl'
          id='why-hero-heading'
        >
          {m.why_listening_hero_title()}{' '}
          <span className='text-azure-595 sm:block'>
            {m.why_listening_hero_title_accent()}
          </span>
        </h1>

        <p className='mt-5 max-w-xl text-base leading-relaxed text-pretty text-(--v2-chip-text) sm:mt-7 sm:text-lg'>
          {m.why_listening_hero_description()}
        </p>

        <div className='mt-8 flex w-full flex-col gap-3 sm:mt-10 sm:w-auto sm:flex-row sm:items-center'>
          <CtaButton
            className='h-13 px-7 py-0 text-base font-semibold shadow-lg shadow-azure-455/25 transition hover:-translate-y-px hover:bg-primary/90 sm:h-14 sm:px-8 sm:text-[17px]'
            label={m.common_try_for_free()}
            variant='default'
          />
          <a
            className='group inline-flex h-13 items-center justify-center gap-2 rounded-full bg-white px-7 text-base font-semibold whitespace-nowrap text-(--v2-text-primary) shadow-sm ring-1 ring-steel-240 transition hover:-translate-y-px hover:ring-steel-520 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-azure-700 sm:h-14 sm:text-[17px]'
            href='#why-compare'
            onClick={(event) => {
              if (
                event.button !== 0 ||
                event.metaKey ||
                event.ctrlKey ||
                event.shiftKey ||
                event.altKey
              ) {
                return;
              }
              const comparison = document.querySelector('#why-compare');
              if (!comparison) return;
              event.preventDefault();
              comparison.scrollIntoView({
                behavior: window.matchMedia('(prefers-reduced-motion: reduce)')
                  .matches
                  ? 'instant'
                  : 'smooth',
                block: 'start',
              });
            }}
          >
            {m.why_listening_hero_compare_link()}
            <ArrowDown
              aria-hidden='true'
              className='size-4 text-steel-700 transition group-hover:translate-y-0.5'
              strokeWidth={2}
            />
          </a>
        </div>

        <ul className='mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm font-medium text-(--v2-chip-text) sm:mt-10'>
          {HIGHLIGHTS.map(([key, highlight]) => (
            <li className='flex items-center gap-1.5' key={key}>
              <Check
                aria-hidden='true'
                className='size-4 text-azure-595'
                strokeWidth={2.4}
              />
              {highlight()}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
